import React, { useEffect, useState } from "react";
import { Form, Input, Select, AutoComplete, DatePicker } from "antd";
import { useDispatch, useSelector } from "react-redux";
import FormItem from "components/fields/FormItem";
import {
    getAssetDetails,
    doAssetCreate,
    doAssetUpdate,
} from "../../../../redux/asset/assetState";
import CrudButtons from "components/button/crudbuttons";
import { requiredField } from "utils/crudSagaHelper";
import { getEmployeeList } from "../../../../redux/employee/employeeState";
import dayjs from "dayjs";

const AssetManagementForm = ({
	submitLabel,
	onCancel,
	...props
}) => {
	const dispatch = useDispatch();

    const recordData = useSelector((state) => state.asset.assetDetails?.data);
	const assetLocationList = useSelector((state) => state.asset.assetDetails.data?.assetLocationList ?? []);
    const assetStatusList = useSelector((state) =>  state.asset.assetDetails.data?.assetStatusList ?? []);
	const assetTypeList = useSelector((state) => state.asset.assetDetails.data?.assetTypeList ?? []);
	const assetConditionList = useSelector((state) => state.asset.assetDetails.data?.assetConditionList ?? []);
    const makeSuggestions = useSelector((state) => state.asset.assetDetails.data?.makeSuggestions ?? []);
    const modelSuggestions = useSelector((state) => state.asset.assetDetails.data?.modelSuggestions ?? []);
    const bundleSNSuggestions = useSelector((state) => state.asset.assetDetails.data?.bundleSNSuggestions ?? []);

	const employees = useSelector((state) => state.employee.employeeList?.items);
    const employeePagination = useSelector((state) => state.employee.employeeList?.pagination);
    const employeeRequesting = useSelector((state) => state.employee.employeeList?.requesting);

	const { Option } = Select;
	const [form] = Form.useForm();
	const [selectedLocationCode, setSelectedLocationCode] = useState(null);
	const { recordId, mode } = props; 

	useEffect(() => {
		dispatch(getAssetDetails(recordId));
	}, [dispatch, recordId]);

	useEffect(() => {
		dispatch(
			getEmployeeList({
				paginate: employeePagination?.per_page || 10,
			})
		);
	}, [dispatch, employeePagination?.per_page]);

	const onSubmit = (formData) => {
		if (formData.eaa_date_provided?.format) {
			formData.eaa_date_provided =
				formData.eaa_date_provided.format("YYYY-MM-DD");
		}

		try {
			if (recordData?.ast_id > 0) {
				dispatch(doAssetUpdate(formData));
			} else {
				dispatch(doAssetCreate(formData));
			}
		} catch (error) {
			console.error("Save failed:", error);
		}
	};

	useEffect(() => {
		if (!recordData) return;

		form.setFieldsValue({
			...recordData,
			eaa_date_provided: recordData?.eaa_date_provided
				? dayjs(recordData.eaa_date_provided)
				: null,
		});

		const location = assetLocationList.find(
			item => item.value === recordData.ast_location_id
		);

		setSelectedLocationCode(location?.code ?? null);
	}, [recordId, recordData, assetLocationList, form]);

	const handleLocationChange = (_, option) => {
		setSelectedLocationCode(option?.code);

		if (option?.default_status_id) {
			form.setFieldValue(
				"ast_status_id",
				option.default_status_id
			);
		}

		if (option?.code !== "ALOC_EMPLOYEE") {
			form.setFieldValue("ast_employee_id", null);
		}
	};

	return (
		<Form
			form={form}
			layout="vertical"
			className={mode !== "view" ? "form" : ""}
			onFinish={onSubmit}
		>
			<FormItem name="ast_id" hidden>
				<Input type="hidden" />
			</FormItem>
				<div className="grid grid-cols-3 gap-4">
					{mode !== "view" ? (
						<FormItem
							name="ast_location_id"
							label="Location"
							rules={[requiredField()]}
						>
							<Select
								className="input-field"
								showSearch
								allowClear
								options={assetLocationList}
								onChange={handleLocationChange}
							/>
						</FormItem>
					) : (
						<div className="p-2">
							<h2 className="text-sm font-medium muted-text">Location</h2>
							<label className="text-base label-value dark:text-white">
								{recordData?.location_name || "-"}
							</label>
						</div>
					)}
					{mode !== "view" ? (
						<FormItem
							name="ast_status_id"
							label="Status"
							rules={[requiredField()]}
						>
							<Select
								className="input-field"
								showSearch
								allowClear
								options={assetStatusList}
							/>
						</FormItem>
					) : (
						<div className="p-2">
							<h2 className="text-sm font-medium muted-text">Status</h2>
							<label className="text-base label-value dark:text-white">
								{recordData?.asset_status_name || "-"}
							</label>
						</div>
					)}
					{selectedLocationCode === "ALOC_EMPLOYEE" && (
						mode !== "view" ? (
								<FormItem
									name="eaa_employee_id"
									label="Assigned Employee"
									rules={[requiredField()]}
								>
									<Select
										showSearch
										className="input-field"
										loading={employeeRequesting}
										placeholder="Select employee"
										optionFilterProp="children"
										optionLabelProp="label"
									>
										{employees?.map((emp) => (
											<Option
												key={emp.emp_id}
												value={emp.emp_id}
												label={emp.employee_name}
											>
												<div className="flex flex-col px-2">
													<span>{emp.employee_name}</span>
												</div>
											</Option>
										))}
									</Select>
								</FormItem>
							) : (
								<div className="p-2">
									<h2 className="text-sm font-medium muted-text">Assigned Employee</h2>
									<label className="text-base label-value dark:text-white">
										{recordData?.assigned_employee_name || "-"}
									</label>
								</div>
							)
					)}
					{selectedLocationCode === "ALOC_EMPLOYEE" && (
						mode !== "view" ? (
							<FormItem
								name="eaa_date_provided"
								label="Date Provided"
								rules={[requiredField()]}
							>
									<DatePicker
										className="input-field w-full"
										format="YYYY-MM-DD"
									/>
							</FormItem>
						) : (
							<div className="p-2">
								<h2 className="text-sm font-medium muted-text">Condition</h2>
								<label className="text-base label-value dark:text-white">
									{recordData?.eaa_date_provided || "-"}
								</label>
							</div>
						)
					)}
					{mode !== "view" ? (
						<FormItem
							name="ast_condition_id"
							label="Condition"
							rules={[requiredField()]}
						>
							<Select
								className="input-field"
								showSearch
								allowClear
								options={assetConditionList}
							/>
						</FormItem>
					) : (
						<div className="p-2">
							<h2 className="text-sm font-medium muted-text">Condition</h2>
							<label className="text-base label-value dark:text-white">
								{recordData?.asset_condition_name || "-"}
							</label>
						</div>
					)}
					{mode !== "view" ? (
						<FormItem
							name="ast_equipment_type_id"
							label="Equipment Type"
							rules={[requiredField()]}
						>
							<Select
								className="input-field"
								showSearch
								allowClear
								options={assetTypeList}
							/>
						</FormItem>
					) : (
						<div className="p-2">
							<h2 className="text-sm font-medium muted-text">Equipment Type</h2>
							<label className="text-base label-value dark:text-white">
								{recordData?.equipment_type_name || "-"}
							</label>
						</div>
					)}
					{mode !== "view" ? (
						<FormItem
							name="ast_make"
							label="Brand"
						>
								<AutoComplete
									options={makeSuggestions}
									filterOption
								>
									<Input className="input-field" />
								</AutoComplete>
						</FormItem>
					) : (
						<div className="p-2">
							<h2 className="text-sm font-medium muted-text">Brand</h2>
							<label className="text-base label-value dark:text-white">
								{recordData?.ast_make || "-"}
							</label>
						</div>
					)}
					{mode !== "view" ? (
						<FormItem
							name="ast_model"
							label="Model"
						>
							<AutoComplete
								options={modelSuggestions}
								filterOption
							>
								<Input className="input-field" />
							</AutoComplete>
						</FormItem>
					) : (
						<div className="p-2">
							<h2 className="text-sm font-medium muted-text">Model</h2>
							<label className="text-base label-value dark:text-white">
								{recordData?.ast_model || "-"}
							</label>
						</div>
					)}
					{mode !== "view" ? (
						<FormItem
							name="ast_individual_serial_number"
							label="Individual SN"
						>
								<Input className="input-field" />
						</FormItem>
					) : (
						<div className="p-2">
							<h2 className="text-sm font-medium muted-text">Individual SN</h2>
							<label className="text-base label-value dark:text-white">
								{recordData?.ast_individual_serial_number || "-"}
							</label>
						</div>
					)}
					{mode !== "view" ? (
						<FormItem
							name="ast_bundled_serial_number"
							label="Bundled SN"
						>
							<AutoComplete
								options={bundleSNSuggestions}
								filterOption
							>
								<Input className="input-field" />
							</AutoComplete>
						</FormItem>
					) : (
						<div className="p-2">
							<h2 className="text-sm font-medium muted-text">Bundled SN</h2>
							<label className="text-base label-value dark:text-white">
								{recordData?.ast_bundled_serial_number || "-"}
							</label>
						</div>
					)}
					{mode !== "view" ? (
						<FormItem
							name="purchase_order_number"
							label="Order ID"
						>
							<Input 
								className="input-field" 
								readOnly
							/>
						</FormItem>
					) : (
						<div className="p-2">
							<h2 className="text-sm font-medium muted-text">Bundled SN</h2>
							<label className="text-base label-value dark:text-white">
								{recordData?.purchase_order_number || "-"}
							</label>
						</div>
					)}
				</div>
				
			<div className="w-full flex justify-end">
				{(mode === "edit") && (
					<CrudButtons
						mode={mode}
						recordId={recordData?.ast_id ?? 0}
						isModal={true}
						modalId={recordData?.ast_id > 0 ? "edit_asset" : "add_asset"}
						cancelLabel="Cancel"
						doHideModal={onCancel}
						submitLabel={submitLabel}
						moduleId={0}
					/>
				)}
			</div>
		</Form>
	);
	
};


export default (AssetManagementForm);
